<template>
  <div class="meeting-infos f-h" :class="classes">
    <FiltersZone v-if="!hideHeader" :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts" class="meeting-infos__switcher-container v-centerer">
        <h2 v-if="asComponent" slot="shortcuts" class="basic-sub-title meeting-infos__page-title">
          {{ pageTitle }}
        </h2>
        <Switcher
          v-if="!elClasses['sm-landscape-size']"
          :key="Object.keys(elClasses).join(',')"
          v-model="viewMode"
          class="meeting-infos__switcher"
          :titles="infosOptViewModeLabels"
          :hints="infosOptViewModeHints"
        />
        <!-- Display the full info shortcut here if there's space available -->
        <ComponentsList
          v-if="!elClasses['sm-landscape-size']"
          :value="infosSortingOptions.filter(opt => opt.key === 'infosOptDisplayFullInfo')"
          @edit="paramsEdit"
        />
      </div>

      <ComponentsList :value="infosSortingOptions" @edit="paramsEdit" />
      <div v-if="elClasses['sm-landscape-size']" class="legend legend--bottom-margin mt10em">
        {{ $t('message.DISPLAY') }}
      </div>
      <Switcher
        v-if="elClasses['sm-landscape-size']"
        v-model="viewMode"
        class="meeting-infos__switcher"
        :titles="infosOptViewModeLabels"
        :hints="infosOptViewModeHints"
      />
    </FiltersZone>
    <!-- Main section -->
    <div ref="infosList" class="meeting-infos__list f-h-below-filters">
      <div v-if="!asWidget && !asComponent" class="meeting-infos__col-headers v-centerer">
        <SortOption class="meeting-infos__col-headers__info" :label="$t('message.INFORMATION')" />
        <SortOption class="meeting-infos__col-headers__author" :label="$t('message.AUTHOR')" />
        <SortOption
          class="meeting-infos__col-headers__release"
          :label="$t('message.INFO_RELEASE_DATE')"
          :is-current="infosOptSortBy.value === 0"
          :is-desc="infosOptSortBy.value === 0 && infosOptSortBy.desc"
          sortable
          @click.native="clickedSortOption(0)"
        />
        <SortOption
          class="meeting-infos__col-headers__removal"
          :label="$t('message.INFO_REMOVAL_DATE')"
          :is-current="infosOptSortBy.value === 1"
          :is-desc="infosOptSortBy.value === 1 && infosOptSortBy.desc"
          sortable
          @click.native="clickedSortOption(1)"
        />
        <SortOption class="meeting-infos__col-headers__files" :label="$t('message.FILES')" />
        <SortOption class="meeting-infos__col-headers__forward" :label="$t('message.INFO_FW')" />
        <SortOption class="meeting-infos__col-headers__delete" :label="$t('message.DELETE')" />
      </div>

      <!-- Real information -->
      <InformationItem
        v-for="info in displayedInfos"
        ref="infoItems"
        :key="info.id"
        :value="info"
        :no-buttons="noButtonsOnItems"
        :no-height-limit="infosOptDisplayFullInformation"
        @openFileViewer="$emit('openFileViewer', $event)"
        @openTeamSelection="$emit('openTeamSelection', $event)"
      />

      <RouterLink
        :to="getInfoCreationLink(0)"
        class="meeting-infos__new generic-border generic-border--text-btn generic-border--blue-border"
      >
        <div class="generic-border__left">
          <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
          <span>{{ $t("message.NEW_INFO") }}</span>
        </div>
        <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
      </RouterLink>

      <div
        v-if="
          viewMode === 0 && (
            (!hideInfoSuggestions && meetingSuggestions.length)
            || (!hideInfoGW && upcomingGw.length)
          )"
        class="legend meeting-infos__additional"
      >
        {{ $t('message.ADDITIONAL_INFO') }}
      </div>

      <!-- Meeting suggestions -->
      <div v-if="!hideInfoSuggestions && meetingSuggestions.length && viewMode === 0">
        <InformationItem
          v-for="info in meetingSuggestions"
          ref="infoItems"
          :key="info.id"
          :value="info"
          :no-buttons="noButtonsOnItems"
          is-suggestion
          :no-height-limit="infosOptDisplayFullInformation"
        />
      </div>

      <!-- Upcoming GW -->
      <div v-if="!hideInfoGW && upcomingGw.length && viewMode === 0">
        <InformationItem
          v-for="info in upcomingGw"
          ref="infoItems"
          :key="info.id"
          :value="info"
          :no-buttons="noButtonsOnItems"
          is-gw
          :no-height-limit="infosOptDisplayFullInformation"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Information page (/meeting-infos)
 * Displays a list of information currently displayed and a list of upcoming information.
 * Can be used as a component from a widget or from the dashboard.
 *
 *
 * @vue-prop {Number} view - Force the viewMode setting
 * @vue-prop {Object} sortBy - Force the sortBy setting
 * @vue-prop {Boolean} asWidget - If true, hide the settings panel on the left
 * @vue-prop {Boolean} hideHeader - If true, Hide the title div
 * @vue-prop {Boolean} cfgTemplateEdition - If this is a widget, prevnt calling FETCH_INFOS
 * @vue-prop {Boolean} noButtonsOnItems - Hide the button on information-item components
 * @vue-prop {Boolean} displayFullInformation - If used as a widget, tick the 'full info' checkbox
 *
 */

import $ from 'jquery';
import cloneDeep from 'lodash/cloneDeep';
import { isEqual, uniq } from 'lodash';
import { mapState } from 'vuex';
// Import components
import ComponentsList from '@/components/components-list.vue';
import Icon from '@/components/icon.vue';
import InformationItem from '@/components/information-item.vue';
import FiltersZone from '@/components/filters-zone.vue';
import SortOption from '@/components/sort-option.vue';
import Switcher from '@/components/switcher.vue';
// Import socket handling utils
import mixinInfosOptions from '@/mixins/page-options/infos.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Vuex utils
import { FETCH_INFOS } from '@/store/action-types';
import { GET_INFOS } from '@/store/mutation-types';
// Import utils
import { itemWidthClasses } from '@/utils/main';
import { addExtraBold, arrowDownLine } from '@/assets/icons';


export default {

  name: 'MeetingInfos',

  components: {
    ComponentsList,
    Icon,
    InformationItem,
    FiltersZone,
    SortOption,
    Switcher,
  },

  mixins: [mixinInfosOptions, mixinSocketHandler],

  props: {
    view: {
      type: Number,
      default: NaN,
    },
    sortBy: {
      type: Object,
      default: null,
    },
    asWidget: Boolean,
    asComponent: Boolean,
    hideHeader: Boolean,
    cfgTemplateEdition: Boolean,
    noButtonsOnItems: Boolean,
    displayFullInformation: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      elClasses: {},
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.INFORMATIONS'),
    };
  },

  computed: {
    classes() {
      return {
        ...this.elClasses,
        'meeting-infos--as-component': this.asComponent,
      };
    },
    /** Boolean: Whether the information regarding upcoming GW should be hidden */
    hideInfoGW() {
      return this.getInfosData && this.getInfosData.params.hideInfoGW === 1;
    },
    /** Boolean: Whether the meeting suggestions should be hidden */
    hideInfoSuggestions() {
      return this.getInfosData && this.getInfosData.params.hideInfoSuggestions === 1;
    },
    /** Array: Info list currently on screen */
    displayedInfos() {
      if (!this.getInfosData) {
        return [];
      }
      const now = this.$moment();
      switch (this.viewMode) {
        case 0: return this.sortInfos(this.getInfosData.infos.filter(i => now.isAfter(i.published)));
        case 1: return this.sortInfos(this.getInfosData.sentInfos);
        case 2: return this.sortInfos(this.getInfosData.infos.filter(i => now.isBefore(i.published)));
        default: return [];
      }
    },
    /** If meeting in progress, display the upcoming occurences of gemba walks */
    upcomingGw() {
      const gws = [];
      const now = this.$moment();
      const dateFormat = this.$t('minDateFormat');
      if (this.$store.state.meeting.meetingData && this.$store.state.meeting.meetingData.upcomingGw) {
        const { upcomingGw = [] } = this.$store.state.meeting.meetingData;

        // Group upcoming GW per GW template id
        const gwPerId = upcomingGw.reduce((all, gw) => ({ ...all, [`${gw.gw}`]: [...all[`${gw.gw}`] || [], gw] }), {});

        Object.keys(gwPerId).forEach((gwTemplate) => {
          const datesArray = uniq(gwPerId[gwTemplate].map(gw => this.$moment(gw.date).format(dateFormat)));
          const dates = datesArray.join(', ');
          const name = gwPerId[gwTemplate][0] ? gwPerId[gwTemplate][0].name : '';
          gws.push({
            id: -gwTemplate,
            text: this.$t('message.INFO_UPCOMING_GW', { name, dates }),
            published: now.format('YYYY-MM-DD'),
            unpublished: now.format('YYYY-MM-DD'),
            teams: [this.currentTeam.id],
            files: [],
          });
        });
      }
      return gws;
    },
    /** Array: Array of current meeting's (if any) suggestions */
    meetingSuggestions() {
      if (!this.hideInfoSuggestions && this.$store.state.meeting.meetingData) {
        const now = this.$moment();
        const { suggestions = [] } = this.$store.state.meeting.meetingData;
        return suggestions.map(suggestion => ({
          ...suggestion,
          published: now.format('YYYY-MM-DD'),
          unpublished: now.format('YYYY-MM-DD'),
        }));
      }
      return [];
    },
    /** Number: Value of view mode switcher */
    viewMode: {
      get() {
        const fromQuery = Number(this.$route.query.infosViewMode);
        const viewMode = this.asWidget ? this.view || fromQuery : fromQuery;
        if (!this.infosOptViewModes[viewMode]) {
          return this.infosOptDefaultViewMode;
        }
        return viewMode;
      },
      set(val) {
        const query = Object.assign({}, this.$route.query);
        query.infosViewMode = val;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
        this.$emit('switchInfoView', val);
      },
    },
    // ========= Config options =========
    infosOptDefaultViewMode() {
      return Number(this.$route.query.infosDefaultViewMode) === 1 ? 1 : 0;
    },
    infosOptSortBy() {
      const def = {
        value: typeof this.$route.query.infosSortBy === 'undefined' ? 0 : Number(this.$route.query.infosSortBy),
        desc: this.$route.query.infosSortDesc === 'true',
      };
      return this.asWidget ? this.sortBy || def : def;
    },
    infosOptDisplayFullInformation() {
      if (this.asWidget) {
        // Infos in widget, use the widget param
        return this.displayFullInformation;
      }
      return this.$route.query.infosOptDisplayFullInfo === 'true';
    },
    ...mapState('file', {
      lastFileDeleted: state => state.lastFileDeleted,
    }),
    ...mapState({
      getInfosData: state => state[GET_INFOS.stateKey],
    }),
  },

  watch: {
    view(val) {
      // Update selected item from a prop
      this.viewMode = val;
    },
    lastFileDeleted() {
      this.refresh();
    },
    isInfoCreationDisplayed() {
      // Refresh the info list if an info been updated
      if (!this.isInfoCreationDisplayed) {
        this.refresh();
        this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.CC_MSGS.MEETING_INFOS });
      }
    },
  },

  created() {
    if (this.cfgTemplateEdition && this.asWidget) {
      return;
    }
    this.refresh();
  },

  mounted() {
    this.handleSocketMessage(SocketMessages.CC_MSGS.MEETING_INFOS, () => { this.refresh(); });
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
  },

  methods: {
    refresh() {
      if (this.cfgTemplateEdition && this.asWidget) {
        return;
      }
      this.$store.dispatch(FETCH_INFOS);
    },
    /** Sort an information array according to params */
    sortInfos(infos) {
      const { value, desc } = this.infosOptSortBy;
      const output = cloneDeep(infos);
      switch (value) {
        case 0:
          return !desc
            ? output.sort((a, b) => a.publishedSortable - b.publishedSortable)
            : output.sort((a, b) => b.publishedSortable - a.publishedSortable);
        case 1:
          return !desc
            ? output.sort((a, b) => a.unpublishedSortable - b.unpublishedSortable)
            : output.sort((a, b) => b.unpublishedSortable - a.unpublishedSortable);
        default:
          return output;
      }
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'infosSortBy') {
        query.infosSortBy = option.value.value;
        query.infosSortDesc = option.value.desc ? 'true' : 'false';
      }
      if (option.key === 'infosOptDisplayFullInfo') {
        query.infosOptDisplayFullInfo = option.value ? 'true' : 'false';
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** A column header has been clicked to sort the list of info */
    clickedSortOption(optionIdx) {
      const option = { key: 'infosSortBy', value: { value: optionIdx, desc: true } };
      if (this.infosOptSortBy.value === optionIdx) {
        option.value.desc = !this.infosOptSortBy.desc;
      }
      this.paramsEdit(option);
    },
    /** Apply style according to size classes, to match the style of the items */
    onSizeChanged(callOnChildren) {
      const { infosList, infoItems } = this.$refs;
      const elt = (infoItems && infoItems[0] && infoItems[0].$el) || infosList;
      const width = elt.offsetWidth;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});

      if (callOnChildren) {
        infoItems.forEach((infoComp) => {
          if (infoComp && infoComp.onSizeChanged) {
            infoComp.onSizeChanged();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";


.meeting-infos {
  &__switcher {
    margin-right: 1rem;
  }

  &__list {
    width: 100%;
    overflow: auto;
    box-sizing: border-box;
  }

  & &__new {
    margin-top: 0.8rem;
    width: max-content;
    background: white;
  }

  &__additional {
    margin-top: 3rem;
  }

  &__col-headers {
    margin-top: 0.8em;
    height: 1.8em;

    &__info {
      flex: 1;
      margin-left: 1rem;
    }

    &__release, &__removal, &__author {
      flex: 0 0 11rem;
    }

    &__files, &__forward, &__delete {
      flex: 0 0 5.72rem;
    }
  }

  &.tablet-size {
    .meeting-infos {
      &__col-headers {
        &__release, &__removal, &__author {
          flex: 0 0 8.5rem;
        }
        &__files, &__forward, &__delete {
          flex: 0 0 4.72rem;
        }
      }
    }
  }

  &.sm-landscape-size {
    .meeting-infos {
      &__col-headers {
        &__release, &__removal, &__author {
          flex: 0 0 6rem;
        }
        &__files, &__forward, &__delete {
          flex: 0 0 3.72rem;
        }
      }
    }
  }

  &.sm-portrait-size {
    .meeting-infos {
      &__col-headers {
        &__release, &__removal, &__author {
          display: none;
        }
      }
    }
  }

  &--as-component {
    background: white;
    border-radius: $main-border-radius;

    .filters-zone__buttons {
      border-bottom: 1px solid $color-gray-border;
      border-top-left-radius: $main-border-radius;
      border-top-right-radius: $main-border-radius;
    }

    .information-item {
      border: 1px solid $color-gray-border;
    }

    .meeting-infos__page-title.basic-sub-title {
      margin-right: 1rem;
    }
  }
}

</style>
